import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { WidgetConsentimientoComponent } from '../widget-consentimiento/widget-consentimiento.component';

export type Marca = 'kaufmann' | 'andes-motor';

interface ConfigMarca {
  codSociedad: string;
  tituloPagina: string;
  favicon: string;
  colorTexto: string;
  colorFondo: string;
  colorBoton: string;
  colorTextoBoton: string;
  /** A pedido explícito (25-sep-2026): identifica desde qué página se hizo el consentimiento
   * -- depende de la página/marca, no del formulario que se muestre. */
  origen: string;
  /**
   * A pedido explícito (24-sep-2026): el widget solo debe autocompletar cuando la página host
   * pasa datos reales de un cliente que ya existe -- nunca con un valor fijo. La prueba puntual
   * del 15-sep-2026 (dato de Juan Perez hardcodeado solo para Kaufmann, para comparar contra
   * Andes Motor) ya cumplió su propósito y se retira -- por defecto ninguna marca autocompleta
   * nada, tal como Andes Motor ya se comportaba.
   */
  datosCliente?: { rut: string; nombres: string; apellidos: string; email: string; telefono: string };
}

/**
 * Colores reales por marca (09-sep-2026, a pedido explícito de Roberto: el
 * widget ya no debe saber de "temas" — solo recibe 4 colores en hex/rgb y se
 * pinta con ellos). Antes estos mismos valores vivían como bloques
 * :host(.tema-kaufmann)/:host(.tema-andes-motor) adentro de
 * consentimiento-widget.component.scss; se movieron acá porque ahora son
 * datos de esta página (la que sabe qué marca es), no del widget.
 */
const CONFIG_POR_MARCA: Record<Marca, ConfigMarca> = {
  kaufmann: {
    codSociedad: 'CONS-KAUF',
    tituloPagina: 'Consentimiento Kaufmann',
    favicon: 'logos/favicon-kaufmann.ico',
    colorTexto: '#ffffff',
    // #0A0C10EB = rgba(10, 12, 16, 0.92) en hex de 8 dígitos (#RRGGBBAA) — mismo
    // color y misma transparencia, solo en hexadecimal (09-sep-2026, a pedido explícito).
    colorFondo: '#0A0C10EB',
    colorBoton: '#00adef',
    colorTextoBoton: '#ffffff',
    origen: 'consentimiento-kaufmann',
  },
  'andes-motor': {
    codSociedad: 'CONS-AM',
    tituloPagina: 'Consentimiento Andes Motor',
    favicon: 'logos/favicon-andes-motor.ico',
    colorTexto: '#11394e',
    // #FFFFFFF7 = rgba(255, 255, 255, 0.97) en hex de 8 dígitos.
    colorFondo: '#FFFFFFF7',
    colorBoton: '#009aed',
    colorTextoBoton: '#ffffff',
    origen: 'consentimiento-andes-motor',
  },
};

/**
 * ESP visual (08-sep-2026, a pedido explícito de Roberto): páginas de muestra que simulan el
 * sitio real donde se va a embeber el widget, para que el fondo/color de base/tipografía del
 * widget se vean "en concordancia" con la página anfitriona — no es el widget solo flotando en
 * blanco. Un mismo componente para las dos marcas (misma forma de página, distinto nav/paleta),
 * en vez de duplicar toda la estructura por cada una.
 *
 * Nomenclatura de ruta: a pedido explícito de Roberto, esto NO se llama "widget-consentimiento"
 * — un "widget" para él es específicamente la versión chica tipo notificación de esquina (como
 * el de Bloomreach). Acá se invoca el mismo componente pero grande, dentro de la página — por
 * eso las rutas son /consentimiento y /consentimiento-andes, sin la palabra "widget".
 *
 * Título/favicon de pestaña (08-sep-2026, a pedido explícito: "que en la pestaña salga el logo
 * de kaufmann y de andes con su nombre correspondiente"): antes toda ruta mostraba el título y
 * el favicon genéricos de `index.html` ("AngularBase" + logo de Angular). Acá se pisan en tiempo
 * de ejecución (favicons reales descargados de kaufmann.cl/andesmotor.cl, en
 * public/logos/favicon-*.ico) — SOLO en esta página; la ruta genérica /consentimiento
 * (ConsentimientoGenericoComponent) queda deliberadamente sin tocar, a pedido explícito, porque
 * no representa a ninguna marca.
 */
@Component({
  selector: 'app-consentimiento-marca',
  standalone: true,
  imports: [WidgetConsentimientoComponent],
  templateUrl: './consentimiento-marca.component.html',
  styleUrls: ['./consentimiento-marca.component.scss'],
})
export class ConsentimientoMarcaComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly titleService = inject(Title);
  private readonly document = inject(DOCUMENT);

  marca: Marca = 'kaufmann';
  config: ConfigMarca = CONFIG_POR_MARCA.kaufmann;

  ngOnInit(): void {
    const marcaRuta = this.route.snapshot.data['marca'] as Marca | undefined;
    this.marca = marcaRuta ?? 'kaufmann';
    this.config = CONFIG_POR_MARCA[this.marca];

    this.titleService.setTitle(this.config.tituloPagina);
    const iconLink = this.document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (iconLink) {
      iconLink.href = this.config.favicon;
    }
  }
}
